import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import DataRoomSignerNdaStatus from './components/DataRoomSignerNdaStatus';
import useCallbackRef from 'hooks/useCallbackRef';
import DataRoomNdaTimeline from './components/DataRoomNdaTimeline';
import {
  DataRoomNdaSigner,
  DataRoomNdaSigning,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { getUserFullName } from 'utils';

import './DataRoomNdaSignerDrawer.styles.scss';

type DataRoomNdaSignerDrawerProps = {
  className?: string;
  dataRoomSigner: DataRoomNdaSigner;
  dataRoomNdaSigning: DataRoomNdaSigning;
  onSendReminder: (signingId: string) => void;
} & SideDrawerProps;

const DataRoomNdaSignerDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  DataRoomNdaSignerDrawerProps
> = (props, ref) => {
  const {
    className,
    dataRoomSigner,
    dataRoomNdaSigning,
    onSendReminder,
    ...rest
  } = props;

  const { userId, user, dataRoomShareableLinkConsumer } = dataRoomSigner || {};

  const {
    _id: signingId,
    status,
    sentDate,
    signedDate,
    rejectionReason,
    timeline,
    numberOfNotSigned,
  } = dataRoomNdaSigning || {};

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames('data-room-nda-signer-drawer', className);

  const firstName = useMemo(
    () =>
      getUserFullName(
        userId ? user?.firstName : dataRoomShareableLinkConsumer?.firstName,
      ),
    [userId, user?.firstName, dataRoomShareableLinkConsumer?.firstName],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes} {...rest}>
      <DataRoomSignerNdaStatus
        signingId={signingId}
        firstName={firstName}
        status={status}
        sentDate={sentDate}
        signedDate={signedDate}
        rejectionReason={rejectionReason}
        onSendReminder={onSendReminder}
      />

      <DataRoomNdaTimeline
        signingId={signingId}
        firstName={firstName}
        status={status}
        numberOfNotSigned={numberOfNotSigned}
        timeline={timeline}
        onSendReminder={onSendReminder}
      />
    </SideDrawer>
  );
};

export default forwardRef(DataRoomNdaSignerDrawer);
